import React, { useEffect, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { TextField } from '@material-ui/core';
import { AutocompleteComponent } from '../../components/Autocomplete/AutocompleteComponent';
import { ModalV2 } from '../../components/Modal/ModalV2';
import { FormContainer } from '../Crm/styles';
import { useForm } from '../../hooks/useForm';
import { useSearch } from '../../hooks/useSearch';
import { INITIAL_VALUES, VALIDATIONS } from './initialValues';
import {
  addElectronicEquipment, editElectronicEquipment
} from '../../redux/actions/electronicEquipment/electronicEquipmentActions';

export const statusOptions = [
  { label: 'ASIGNADO', value: 'ASSIGNED' },
  { label: 'EN REPARACIÓN', value: 'REPAIR' },
  { label: 'ACTIVO', value: 'ACTIVE' }
];

export default function ElectronicEquipmentForm({
  isOpen,
  onClose,
  data = null,
  selectedStation = null,
}) {
  const dispatch = useDispatch();
  const { setSnackbar } = useSearch();

  const { equipmentTypes, stationsList, isLoadingComp, equipment } = useSelector(state => ({
    equipmentTypes: state.electronicEquipment?.equipmentTypes,
    stationsList: state.companies?.stationsList,
    isLoadingComp: state.search?.isLoadingComp,
    equipment: state.electronicEquipment?.equipment,
  }), shallowEqual);

  const {
    values, onChange, setValues, reset, errors, validate,
  } = useForm(INITIAL_VALUES, VALIDATIONS);

  const {
    idEquipmentType, number, code, idStation,
    serialNumber, status
  } = values;

  // Derived state
  const isEditMode = data !== null;
  const loading = isEditMode ? isLoadingComp?.edit : isLoadingComp?.add;
  const title = `${isEditMode ? 'Modificar' : 'Nuevo'} equipo electrónico`;
  const submitText = isEditMode ? 'Guardar' : 'Agregar';

  const handleClose = useCallback(() => {
    reset();
    onClose();
  }, [reset, onClose]);

  const checkDuplicate = useCallback((formValues) => {
    // Exclude current record in edit mode
    const filtered = isEditMode ? equipment.filter(eq => eq.id !== data.id) : equipment;

    // formValues ya viene normalizado (trim) desde handleSubmit;
    // norm normaliza los valores almacenados, que pueden ser null o traer espacios.
    const norm = (v) => (v ?? '').trim();

    const duplicateFields = [];
    if (filtered.some(eq => eq.number === formValues.number)) duplicateFields.push('Número');
    if (filtered.some(eq => norm(eq.code) === formValues.code)) duplicateFields.push('Clave');
    if (filtered.some(eq => norm(eq.serialNumber) === formValues.serialNumber)) duplicateFields.push('No. de serie');

    if (duplicateFields.length) {
      setSnackbar(`Ya existe un equipo con el mismo: ${duplicateFields.join(', ')} en esta planta.`, "danger");
      return true;
    }
    return false;
  }, [equipment, isEditMode, data?.id]);

  const handleNumberChange = useCallback((e) => {
    const val = e.target.value;
    if (val === '' || /^\d+$/.test(val)) onChange(e);
  }, [onChange]);

  const handleSubmit = () => {
    validate([], () => {
      const payload = {
        idStation: idStation.value,
        idEquipmentType: idEquipmentType.value,
        number: Number(number),
        code: code.trim(),        
        serialNumber: serialNumber.trim(),
        status: status.value
      };

      const isDuplicate = checkDuplicate(payload);
      if (isDuplicate) return;

      if (isEditMode) {
        dispatch(editElectronicEquipment(data.id, payload, handleClose));
      } else {
        dispatch(addElectronicEquipment(payload, handleClose));
      }
    });
  };

  // Safe to use [] because this component is unmounted/remounted on each modal open
  // via conditional rendering: {isOpen && <ElectronicEquipmentForm />}
  useEffect(() => {
    if (isEditMode && data) {
      // Edit mode: populate with equipment data
      const equipmentType = equipmentTypes.find(item => item.id === data.idEquipmentType);
      const equipmentStation = stationsList.find(item => item.id === data.idStation);
      const equipmentStatus = statusOptions.find(item => item.value === data.status);

      setValues({
        idEquipmentType: equipmentType || null,
        number: data.number || '',
        code: data.code || '',
        idStation: equipmentStation || null,
        serialNumber: data.serialNumber || '',
        status: equipmentStatus || null
      });
    } else {
      // Add mode: start with initial values, optionally with station
      const initialData = selectedStation
        ? { ...INITIAL_VALUES, idStation: selectedStation }
        : INITIAL_VALUES;
      setValues(initialData);
    }
  }, []);

  return (
    <ModalV2
      open={isOpen}
      onClose={handleClose}
      title={title}
      submitBtnText={submitText}
      onSubmit={handleSubmit}
      toRight
      isLoading={loading}
    >
      <FormContainer style={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 15
      }}>
        <AutocompleteComponent
          label="Planta"
          name="idStation"
          value={idStation}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={{
            error: !!errors.idStation,
            helperText: errors.idStation,
          }}
          options={stationsList}
          sort
          disabled
        />

        <AutocompleteComponent
          label="Equipo"
          name="idEquipmentType"
          value={idEquipmentType}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={{
            error: !!errors.idEquipmentType,
            helperText: errors.idEquipmentType,
          }}
          options={equipmentTypes}
          sort
        />

        <TextField
          fullWidth
          label="Número"
          placeholder="111222"
          name="number"
          value={number}
          onChange={handleNumberChange}
          variant="outlined"
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          error={!!errors.number}
          helperText={errors.number}
        />

        <TextField
          fullWidth
          label="Clave"
          name="code"
          value={code}
          onChange={onChange}
          variant="outlined"
          error={!!errors.code}
          helperText={errors.code}
        />

        <TextField
          fullWidth
          label="No. serie"
          placeholder="111222"
          name="serialNumber"
          value={serialNumber}
          onChange={onChange}
          variant="outlined"
          error={!!errors.serialNumber}
          helperText={errors.serialNumber}
        />

        <AutocompleteComponent
          label="Estado"
          name="status"
          value={status}
          handleValues={setValues}
          fullWidth
          variant="outlined"
          textFieldProps={{
            error: !!errors.status,
            helperText: errors.status,
          }}
          options={statusOptions}
          sort
        />
      </FormContainer>
    </ModalV2>
  );
}